<template>
	<div class="modal-wrapper wizard-email">
		<div class="email-container bg-white">
			<div class="modal-text">
				<div class="email-modal-title">
					{{ content.modalTitle }}
				</div>
				<div class="email-modal-text">
					{{ content.modalSubtitle }}
				</div>
				<ValidationObserver v-slot="{ invalid }" tag="div" class="flex flex-col justify-between form" @submit.prevent>
					<div class="flex flex-col input-wrapper">
						<SVGIcon icon-name="wizard/sms" fill="#F8F9FB" />
						<TextInput
							v-model="email"
							:placeholder="content.inputPlaceholderText"
							:is-hidden-label="true"
							name="Email"
							rules="required|email"
							class="email-step-input"
							:class="email && 'blured'"
						/>
						<div class="email-step-text-helper">
							{{ content.inputLabelText }}
						</div>
					</div>
					<div class="flex checkbox-wrapper">
						<Checkbox
							v-model="isReceiveNews"
							:label="content.checkboxLabelText"
							class="checkbox-wizard"
						/>
					</div>
					<div class="flex flex-col items-center">
						<div class="button-wrapper">
							<continue-button v-loading="loading" :is-show-arrow="false" :disabled="invalid" @click="handleSignUp" />
						</div>
						<span class="skip-text" @click="onClose">
							{{ content.skipText }}
						</span>
					</div>
				</ValidationObserver>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { ModalMixin } from '~/assets/vue/mixins'
import { TextInput, Checkbox, SVGIcon } from '~/components/interface'
import ContinueButton from '~/components/wizard-new/common/ContinueButton'
import content from '~/static/wizard-content/WizardEmail.json'
import analytics from '~/assets/util/analytics'

export default {
	name: 'ModalWizardEmail',
	components: {
		TextInput,
		Checkbox,
		ValidationObserver,
		ContinueButton,
		SVGIcon
	},
	mixins: [ModalMixin],
	props: {
		options: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			email: '',
			isReceiveNews: true,
			content: content[0],
			loading: false
		}
	},
	methods: {
		...mapMutations({ setEmail: 'purchase/setEmail', setUserRegistered: 'purchase/setUserRegistered' }),
		...mapActions({
			analyticsTrack: 'analytics/track',
			createUser: 'purchase/createUser'
		}),
		handleNext () {
			this.onClose()
		},
		handleSignUp () {
			this.loading = true
			this.requestErrorMessage = ''

			this.createUser({ email: this.email, skipVerificationEmail: true })
				.then(() => {
					this.analyticsTrack({
						event: analytics.WizardPurchaseUserStartedPurchaseFlow.event,
						data: analytics.WizardPurchaseUserStartedPurchaseFlow.getData({
							email: this.email,
							is_receive_news: this.isReceiveNews
						})
					})
					this.loading = false
					this.handleNext()
				})
				.catch(error => this.onError(error, true))
		},
		onError (error, showMsg = false) {
			const isCustomToast = error.cause && error.cause === 'auth/email-already-in-use'
			const toastMessage = this.$tc(`${isCustomToast ? 'errors.emailAlreadyInUseMessage' : 'errors.tryAgainLater'}`)
			const customToastObj = {
				title: this.$tc('errors.emailAlreadyInUse'),
				actionTitle: this.$tc('components.authHeader.signIn'),
				actionCallback: (close) => {
					this.onClose()
					close()
					this.$router.push({ name: 'login' })
				},
				isCustomAction: true,
				isCustomToast,
				customClass: 'sign-up-toast-action'
			}

			this.loading = false

			if (showMsg) {
				this.$toast.error({
					message: toastMessage,
					...(isCustomToast && customToastObj)
				})
			}

			this.$log.error('Sign up step error', error)
		}
	}
}
</script>

<style lang="scss">
.wizard-email .email-container {
	box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.11);
	border-radius: 20px;
	border: none;
	max-width: 410px;
	width: calc(100% - 40px);
	padding: 41px 29px 28px;
	.email-modal-title {
		font-size: 24px;
		line-height: 28px;
		font-weight: 500;
		color: #4D4D4F;
		text-align: center;
		margin-bottom: 6px;
	}
	.email-modal-text {
		font-size: 16px;
		line-height: 20px;
		color: #8A8B9F;
		text-align: center;
		margin-bottom: 38px;
	}
	.text-input-wrap {
		position: relative;
		.text-input {
			height: 50px;
			border-radius: 7px;
			background-color: #F8F9FB;
			border: 1px solid #C8CED9;
			color: #2B2B2B;
			font-size: 16px;
			padding-left: 60px;
			&:focus {
				border-color: #009BC9;
			}
		}
	}
	.input-wrapper {
		position: relative;
		margin-bottom: 39px;
		svg {
			position: absolute;
			left: 14px;
			top: 6px;
			z-index: 1;
		}
		.email-step-text-helper {
			margin-top: 9px;
			font-size: 14px;
			line-height: 17px;
			color: #8A8B9F;
		}
	}
	.checkbox-wrapper {
		margin-bottom: 34px;
		.pointer {
			padding-right: 80px;
			@include respond-below(phone) {
				font-size: 16px;
				padding-right: 0;
			}
		}
	}
	.button-wrapper {
		margin-bottom: 27px;
		.continue-button  {
			width: 224px;
		}
	}
	.skip-text {
		color: #2B2B2B;
		font-size: 16px;
		line-height: 20px;
		cursor: pointer;
		transition: color .2s ease;
		&:hover {
			color: #009BC9;
		}
	}
}

.checkbox-container.checkbox-wizard {
	align-items: flex-start;
	gap: 14px;
	input[type=checkbox] {
		+ label.checkbox-checkmark {
			width: 20px;
			height: 20px;
			background: #FFFFFF;
			border: 1px solid #009BC9;
			border-radius: 4px;
			&:after {
				border-color: #FFFFFF;
				border-width: 2px;
				left: 5px;
				top: 5px;
				width: 7px;
				height: 3px;
			}
		}
		&:checked {
			+ label.checkbox-checkmark {
				background: #009BC9;
				border-color: #FFFFFF;
			}
		}
	}
}
</style>
